import type { IconName } from '@/primer/components';

/**
 * Task types the creator pilot can be completed against. Only Instagram
 * deliverables are shipped for now — other known types (e.g. TikTok) still get
 * correct display metadata below, but are not submittable via this flow.
 */
export const SUPPORTED_TASK_TYPES = ['POST_ON_INSTAGRAM_FEED', 'POST_ON_INSTAGRAM_STORY'] as const;

export interface TaskDisplay {
  name: string;
  icon: IconName;
  /** Platform name used in the confirmation copy ("post this content in your … account"). */
  platform: string;
}

const TASK_DISPLAY: Record<string, TaskDisplay> = {
  POST_ON_INSTAGRAM_FEED: { name: 'Post on Instagram', icon: 'instagram', platform: 'Instagram' },
  POST_ON_INSTAGRAM_STORY: {
    name: 'Post on Instagram Story',
    icon: 'instagram',
    platform: 'Instagram',
  },
  // Known but not supported in the pilot — rendered with correct branding.
  POST_ON_TIKTOK: { name: 'Post on TikTok', icon: 'tiktok', platform: 'TikTok' },
};

/** Whether a task type is supported in the pilot. */
export const isSupportedTaskType = (type: string): boolean =>
  (SUPPORTED_TASK_TYPES as readonly string[]).includes(type.toUpperCase());

/**
 * Display metadata for a campaign task type. Falls back to a title-cased version
 * of the raw type so unknown/unsupported types still render sensibly.
 */
export function getTaskDisplay(type: string): TaskDisplay {
  const known = TASK_DISPLAY[type.toUpperCase()];
  if (known) return known;
  return {
    name: type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    icon: 'tasks',
    platform: 'social media',
  };
}
